import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import Page from 'commons/page'
import Block from 'components/block'
import { useAuthProvider } from 'contexts/auth'
import { useAppDispatch, useAppSelector } from 'hooks'
import AccessDenied from 'pages/access-denied'
import {
  fetchContractById,
  selectContractById,
  selectContractByIdFailed,
  selectContractByIdLoading,
  selectFeatureByIdFailed,
} from 'stores/contract'

import SubPageHeader from '../../components/sub-page-header'
import ActiveInventoryList from '../../features/contracts/list/active-inventory-list'
import { ActiveInventoryItem } from '../../schema/contract'
import { UserRole } from '../../schema/user'
import PropertyMapView from '../../components/property-map-view'

const ActiveInventoriesMap = () => {
  const { claims } = useAuthProvider()
  const isOwner = useMemo(() => claims?.role === UserRole.OWNER, [claims?.role])
  const contract = useAppSelector(selectContractById)
  const contractLoading = useAppSelector(selectContractByIdLoading)
  const contractFailed = useAppSelector(selectContractByIdFailed)
  const featureFailed = useAppSelector(selectFeatureByIdFailed)
  const dispatch = useAppDispatch()
  const { id } = useParams<{ id: string }>()

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
  const [isMobile, setIsMobile] = useState<boolean>(false)
  const [currentPanelIndex, setCurrentPanelIndex] = useState<number>(0)

  useEffect(() => {
    if (!claims?.role) {
      return
    }

    if (!id) {
      return
    }

    dispatch(fetchContractById({ role: claims?.role, id }))
  }, [dispatch, claims?.role, id])

  const reFetch = () => {
    dispatch(fetchContractById({ role: claims!.role!, id: id! }))
  }

  const data = useMemo(() => {
    return contract
  }, [contract])

  const HasApiError = useMemo(() => {
    return contractFailed || featureFailed
  }, [contractFailed, featureFailed])

  const assignNumbers = (items: ActiveInventoryItem[]) => {
    let currentItemNumber = 1
    return items.map((item) => {
      const isVisible = item.isVisible !== undefined ? item.isVisible : true
      const isLocated = item.lat && item.lon
      const itemNumberLabel =
        isVisible && isLocated ? String(currentItemNumber++) : '-'
      return {
        ...item,
        itemNumberLabel,
      }
    })
  }

  const { activeInventories, position, displayConfig } = useMemo(() => {
    if (!data) {
      return {
        position: null,
        activeInventories: [],
        displayConfig: null,
      }
    }
    const numberedActiveInventories = assignNumbers(
      (data.activeInventory?.items ?? []).filter(
        (item) => item.lat && item.lon && item.isVisible !== false
      )
    )

    const config = {
      address: data.activeInventory?.config.address,
      square: data.activeInventory?.config.square,
      buildYearMonth: data.activeInventory?.config.buildYearMonth,
    }

    return {
      position: {
        lat: Number(data.activeInventory?.config.lat) ?? null,
        lon: Number(data.activeInventory?.config.lon) ?? null,
      },
      activeInventories: numberedActiveInventories,
      displayConfig: config,
    }
  }, [data])

  const visibleInventories = useMemo(() => {
    return activeInventories.filter(
      (item) => item.lat && item.lon && item.isVisible
    )
  }, [activeInventories])

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1200)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => {
      window.removeEventListener('resize', checkMobile)
    }
  }, [])

  const handleItemClick = (item: ActiveInventoryItem) => {
    if (!item.lat || !item.lon || !item.isVisible) {
      return
    }

    const itemIndex = activeInventories.findIndex((i) => i.id === item.id)
    if (itemIndex !== -1) {
      setSelectedItemId(`marker-${itemIndex}`)
      // モバイルの場合はパネルインデックスも更新
      if (isMobile) {
        const visibleIndex = visibleInventories.findIndex(
          (i) => i.id === item.id
        )
        if (visibleIndex !== -1) {
          setCurrentPanelIndex(visibleIndex)
        }
      }
    }
  }

  const currentVisibleItem = useMemo((): ActiveInventoryItem | null => {
    if (!isMobile || visibleInventories.length === 0) return null
    return visibleInventories[currentPanelIndex] || null
  }, [isMobile, visibleInventories, currentPanelIndex])

  return (
    <>
      {HasApiError && <AccessDenied />}
      {!HasApiError && (
        <Page
          title={`周辺の売出物件 - 地図`}
          loading={contractLoading}
          type="user"
          infiniteScroll
          customHeader={<SubPageHeader />}
        >
          {activeInventories.length !== 0 && (
            <Block>
              <PropertyMapView
                pageTitle="周辺の売出物件 - 地図"
                sectionTitle="周辺の売出物件"
                items={activeInventories}
                position={position}
                displayConfig={displayConfig}
                visibleItems={visibleInventories}
                currentVisibleItem={currentVisibleItem}
                selectedItemId={selectedItemId || undefined}
                isMobile={isMobile}
                onItemClick={handleItemClick}
                currentPanelIndex={currentPanelIndex}
                onPanelIndexChange={setCurrentPanelIndex}
                renderList={({
                  items,
                  currentVisibleItem,
                  isMobile,
                  selectedItemId,
                  onItemClick,
                }) => (
                  <ActiveInventoryList
                    list={items}
                    contractId={id!}
                    isOwner={isOwner}
                    reFetch={reFetch}
                    isVertical={true}
                    onItemClick={onItemClick}
                    currentVisibleItem={currentVisibleItem}
                    isMobile={isMobile}
                    selectedItemId={selectedItemId}
                    background="white"
                  />
                )}
                getAccordionTitle={(hasConditions) =>
                  hasConditions
                    ? '売出物件と成約事例の表示条件'
                    : '表示についての注意'
                }
                ownerNotice={
                  !isOwner
                    ? '売主様の画面に表示するかを物件毎に設定できます。'
                    : undefined
                }
              />
            </Block>
          )}
        </Page>
      )}
    </>
  )
}

export default ActiveInventoriesMap
